import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import ScreenTooSmall from "../../components/ui/ScreenTooSmall";
import { Monitor } from "lucide-react";
import { AVATARS, AVATARS_PER_PAGE, getAvatarUrl } from "../../avatars";
import { useConfirm } from "../../components/ui/Confirm";
import PlayReconnectModal from "./PlayReconnectModal";

const API = process.env.REACT_APP_API_URL;

function Play() {
  const { code } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();

  const MIN_WIDTH = 1024;
  const [isTooSmall, setIsTooSmall] = useState(false);
  const [pseudo, setPseudo] = useState("");
  const [password, setPassword] = useState("");
  const [avatarId, setAvatarId] = useState("");
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [players, setPlayers] = useState([]);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [reconnectPassword, setReconnectPassword] = useState("");
  const [reconnectError, setReconnectError] = useState(null);
  const [reconnectLoading, setReconnectLoading] = useState(false);
  const [myTeamNameProposal, setMyTeamNameProposal] = useState("");
  const [showAllAvatars, setShowAllAvatars] = useState(false);

  // (si aucun joueur existant, celui qui join devient joueur 1)
  // pool "rapide" : 25 avatars (tu peux ajuster)
  const QUICK_AVATAR_COUNT = 25;

  // les 25 premiers
  const quickAvatars = AVATARS.slice(0, QUICK_AVATAR_COUNT);

  const handleRandomAvatar = () => {
    const pool = avatarPool; // short ou full selon le mode
    const randomIndex = Math.floor(Math.random() * pool.length);
    const avatar = pool[randomIndex];

    setAvatarId(avatar.id);

    const targetPage = Math.floor(randomIndex / AVATARS_PER_PAGE);
    setPage(targetPage);
  };

  const avatarPool = showAllAvatars ? AVATARS : quickAvatars;

  const totalPages = Math.ceil(avatarPool.length / AVATARS_PER_PAGE);

  const start = page * AVATARS_PER_PAGE;
  const currentAvatars = avatarPool.slice(start, start + AVATARS_PER_PAGE);

  useEffect(() => {
    const check = () => setIsTooSmall(window.innerWidth < MIN_WIDTH);
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  // Récupère les joueurs déjà présents dans la session
  useEffect(() => {
    const fetchPlayers = async () => {
      try {
        setLoadingPlayers(true);
        const res = await axios.get(`${API}/api/sessions/${code}/players`);
        setPlayers(res.data.players || []);
        setSessionStatus(res.data.sessionStatus || null);
      } catch (e) {
        // Code session inexistant → retour à l'accueil avec message
        if (e.response?.status === 404) {
          navigate(`/play`, { state: { error: "Code de session invalide ou inexistant." } });
          return;
        }
        console.error("Erreur fetchPlayers", e);
      } finally {
        setLoadingPlayers(false);
      }
    };

    fetchPlayers();
  }, [code]);

  // Auto-reconnexion si playerId déjà connu dans ce navigateur
  useEffect(() => {
    const storedId = sessionStorage.getItem(`playerId:${code}`);
    if (!storedId || !players.length || !sessionStatus) return;

    const exists = players.some((p) => p.id === Number(storedId));
    if (!exists) {
      // Le playerId ne correspond plus à aucun joueur → reset
      sessionStorage.removeItem(`playerId:${code}`);
      sessionStorage.removeItem(`playerPseudo:${code}`);
      sessionStorage.removeItem(`playerAvatar:${code}`);
      sessionStorage.removeItem(`playerAvatarLabel:${code}`);
      return;
    }

    // On renvoie direct vers waiting/game
    if (sessionStatus === "in_progress") {
      navigate(`/game/${code}`);
    } else {
      navigate(`/waiting/${code}`);
    }
  }, [code, players, sessionStatus, navigate]);

  useEffect(() => {
    setPage(0);
  }, [showAllAvatars]);


  // Fallback mobile (bloquant)
  if (isTooSmall) {
    return (
      <ScreenTooSmall
        minWidth={MIN_WIDTH}
        title="Rejoindre la salle"
        subtitle={`Le jeu n'est pas disponible sur mobile (largeur ≥ ${MIN_WIDTH}px).`}
      />
    );
  }

  const handlePrev = () => setPage((p) => (p - 1 + totalPages) % totalPages);
  const handleNext = () => setPage((p) => (p + 1) % totalPages);

  const handleJoin = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const selectedAvatar = AVATARS.find((a) => a.id === avatarId) || null;
      const avatarSeed = selectedAvatar ? selectedAvatar.seed : null;
      const avatarLabel = selectedAvatar ? selectedAvatar.label : null;

      const res = await axios.post(`${API}/api/play/join`, {
        code,
        pseudo,
        password,
        avatar: avatarSeed,
        avatarLabel,
      });

      const { player } = res.data;

      sessionStorage.setItem(`playerId:${code}`, String(player.id));
      sessionStorage.setItem(`playerPseudo:${code}`, player.pseudo);
      if (avatarSeed) sessionStorage.setItem(`playerAvatar:${code}`, avatarSeed);
      if (avatarLabel)
        sessionStorage.setItem(`playerAvatarLabel:${code}`, avatarLabel);

      // Proposition de nom d'équipe (tous les joueurs)
      if (myTeamNameProposal.trim()) {
        try {
          await axios.post(`${API}/api/sessions/${code}/team-name/propose`, {
            playerId: player.id,
            teamName: myTeamNameProposal.trim(),
          });
        } catch (e) {
          console.warn("Impossible de proposer le nom d'équipe", e);
          // non bloquant
        }
      }


      navigate(`/waiting/${code}`);
    } catch (err) {
      setError(err.response?.data?.message || "Impossible de rejoindre la partie");
    }
  };

  // Ouverture de la modale de reconnexion pour un joueur existant
  const openReconnect = (player) => {
    setSelectedPlayer(player);
    setReconnectPassword("");
    setReconnectError(null);
  };

  const closeReconnect = () => {
    setSelectedPlayer(null);
    setReconnectPassword("");
    setReconnectError(null);
    setReconnectLoading(false);
  };

  // Reconnexion normale (avec mot de passe)
  const handleReconnect = async () => {
    if (!selectedPlayer) return;
    setReconnectError(null);
    setReconnectLoading(true);

    try {
      const res = await axios.post(`${API}/api/play/reconnect`, {
        code,
        playerId: selectedPlayer.id,
        password: reconnectPassword,
        bypass: false,
      });

      const { player } = res.data;

      sessionStorage.setItem(`playerId:${code}`, String(player.id));
      sessionStorage.setItem(`playerPseudo:${code}`, player.pseudo);
      if (player.avatar) sessionStorage.setItem(`playerAvatar:${code}`, player.avatar);
      if (player.avatarLabel)
        sessionStorage.setItem(`playerAvatarLabel:${code}`, player.avatarLabel);

      // On redirige en fonction de l'état de la session
      if (sessionStatus === "in_progress") {
        navigate(`/game/${code}`);
      } else {
        navigate(`/waiting/${code}`);
      }
    } catch (err) {
      setReconnectError(err.response?.data?.message || "Impossible de vous reconnecter.");
    } finally {
      setReconnectLoading(false);
    }
  };

  //  Bypass mot de passe
  const handleBypass = async () => {
    if (!selectedPlayer) return;

    const ok = await confirm({
      title: "Forcer la connexion ?",
      message: `⚠️ Vous allez forcer la connexion sur le profil "${selectedPlayer.pseudo}". 
 Si cette personne est encore connectée, elle sera déconnectée. Cette action est irréversible.

Êtes-vous sûr(e) de vouloir continuer ?`,
      confirmText: "Continuer",
      cancelText: "Annuler",
      danger: true, // bouton rouge (optionnel)
    });

    if (!ok) return;

    setReconnectError(null);
    setReconnectLoading(true);

    try {
      const res = await axios.post(`${API}/api/play/reconnect`, {
        code,
        playerId: selectedPlayer.id,
        bypass: true,
      });

      const { player } = res.data;

      sessionStorage.setItem(`playerId:${code}`, String(player.id));
      sessionStorage.setItem(`playerPseudo:${code}`, player.pseudo);
      if (player.avatar) sessionStorage.setItem(`playerAvatar:${code}`, player.avatar);
      if (player.avatarLabel)
        sessionStorage.setItem(`playerAvatarLabel:${code}`, player.avatarLabel);

      if (sessionStatus === "in_progress") {
        navigate(`/game/${code}`);
      } else {
        navigate(`/waiting/${code}`);
      }
    } catch (err) {
      setReconnectError(
        err.response?.data?.message ||
        "Impossible de forcer la connexion pour ce joueur."
      );
    } finally {
      setReconnectLoading(false);
    }
  };

  const creationLocked = sessionStatus === "in_progress";

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white flex items-center justify-center px-6 py-16">
      <div className="w-full max-w-3xl relative">
        <div className="bg-white/10 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-xl space-y-6">
          <div className="-mt-1 rounded-xl border border-white/10 bg-black/30 px-3 py-2 text-[12px] text-gray-200 flex items-start gap-2">
            <Monitor className="h-4 w-4 mt-0.5 text-orange-300" />
            <span>
              Expérience optimisée pour ordinateur (largeur ≥ {MIN_WIDTH}px).
              Mobile non supporté.
            </span>
          </div>
          {/* Header */}
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">Rejoindre la salle</h1>
            <span className="text-xs px-2 py-1 rounded-full bg-white/10 border border-white/10 text-indigo-300 font-mono uppercase tracking-wider">
              Code session - {code}
            </span>
          </div>

          {/* Bloc joueurs existants (reconnexion) */}
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <p className="text-sm text-gray-300 font-medium">
                Joueurs déjà dans cette session
              </p>
              {loadingPlayers && (
                <span className="text-[11px] text-gray-400">Chargement…</span>
              )}
            </div>

            {players.length === 0 ? (
              <p className="text-xs text-gray-500">
                Aucun joueur encore enregistré pour cette session. Vous serez le premier.
              </p>
            ) : (
              <div className="mt-2 grid sm:grid-cols-3 gap-3">
                {players.map((p) => (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => openReconnect(p)}
                    className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/5 px-3 py-2 hover:border-indigo-400/70 hover:bg-white/10 transition"
                  >
                    <div className="h-9 w-9 rounded-full overflow-hidden bg-indigo-500/20 flex items-center justify-center font-semibold text-sm">
                      {p.avatar ? (
                        <img
                          src={getAvatarUrl(p.avatar)}
                          alt={p.pseudo}
                          className="w-full h-full object-contain"
                        />
                      ) : (
                        <span>{p.pseudo?.slice(0, 1)?.toUpperCase() || "?"}</span>
                      )}
                    </div>
                    <div className="flex-1 text-left">
                      <div className="text-sm font-semibold truncate">
                        {p.pseudo}
                      </div>
                      <div className="text-[11px] text-gray-400 truncate">
                        Reconnexion possible
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {players.length > 0 && (
              <p className="mt-2 text-[11px] text-gray-500">
                Vous avez déjà rejoint cette session ? Cliquez sur votre carte ci-dessus
                pour vous reconnecter avec votre mot de passe.
              </p>
            )}
          </div>

          {/* Séparateur */}
          <div className="border-t border-white/10 my-2" />

          {/* Formulaire nouveau joueur */}
          {!creationLocked && (
            <>
              <p className="text-gray-400 text-sm">
                Créez votre profil en choisissant un{" "}
                <span className="text-indigo-300">pseudo</span>, un{" "}
                <span className="text-indigo-300">mot de passe de reconnexion</span> et un{" "}
                <span className="text-indigo-300">avatar</span>.
              </p>

              <form onSubmit={handleJoin} className="space-y-6">
                {/* Input proposition visible pour TOUS */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Proposer un nom d'équipe (optionnel)
                  </label>
                  <input
                    type="text"
                    value={myTeamNameProposal}
                    onChange={(e) => setMyTeamNameProposal(e.target.value)}
                    placeholder="Ex : Les Fantômes du Réseau"
                    className="w-full p-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Vous pourrez voter pour votre nom préféré dans la salle d'attente. Si personne ne propose de nom, un nom sera généré automatiquement.
                  </p>
                </div>


                {/* Pseudo + mot de passe sur la même row */}
                <div className="flex flex-col md:flex-row gap-4 w-full">
                  {/* <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Pseudo
                    </label>
                    <input
                      type="text"
                      value={pseudo}
                      onChange={(e) => setPseudo(e.target.value)}
                      placeholder="Ex : Alex"
                      className="w-full p-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      required
                    />
                  </div> */}

                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Pseudo
                    </label>
                    <input
                      type="text"
                      value={pseudo}
                      onChange={(e) => {
                        const clean = e.target.value
                          .replace(/[^a-zA-Z0-9À-ÿ _\-]/g, "")  // autorise lettres (accents inclus), chiffres, espace, tiret
                          .slice(0, 20);
                        setPseudo(clean);
                      }}
                      placeholder="Ex : Alex"
                      maxLength={20}
                      className="w-full p-3 bg-gray-900 border border-gray-700 rounded-lg text-white
               placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      required
                    />
                    <p className="text-[11px] text-gray-500 mt-1 text-right">
                      {pseudo.length}/20
                    </p>
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-300 mb-1">
                      Mot de passe (pour vous reconnecter si besoin)
                    </label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Choisissez un mot de passe simple à retenir"
                      className="w-full p-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                      required
                    />
                    <p className="text-[11px] text-gray-500 mt-1">
                      Il est uniquement utilisé pour ce jeu, pendant la durée de la session.
                    </p>

                  </div>
                </div>


                {/* Avatar selector */}
                <div className="relative bg-black/30 border border-white/10 rounded-xl p-4">
                  <div className="flex items-center justify_between mb-3">
                    <div className="flex items-center justify-between mb-3 gap-3">
                      <div className="flex items-center gap-2">
                        <h2 className="text-sm font-semibold text-white uppercase tracking-[0.15em]">
                          Avatar
                        </h2>
                        <span className="text-[11px] text-gray-400">
                          {`Page ${page + 1} / ${totalPages}`}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={handleRandomAvatar}
                          className="px-3 py-1.5 rounded-full text-[11px] font-semibold bg-white/10 hover:bg-white/15 border border-white/10 transition"
                          title="Choisir un avatar au hasard"
                        >
                          🎲 Aléatoire
                        </button>

                        <button
                          type="button"
                          onClick={() => setShowAllAvatars((v) => !v)}
                          className="px-3 py-1.5 rounded-full text-[11px] font-semibold bg-black/40 hover:bg-black/55 border border-white/10 transition"
                        >
                          {showAllAvatars ? "Réduire" : "Voir plus"}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Boutons navigation */}
                  <button
                    type="button"
                    onClick={handlePrev}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 flex items-center justify-center text-sm"
                  >
                    ←
                  </button>
                  <button
                    type="button"
                    onClick={handleNext}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 hover:bg-black/80 border border-white/20 flex items-center justify-center text-sm"
                  >
                    →
                  </button>

                  {/* Grille avatars */}
                  <div className="mt-1 grid grid-cols-5 gap-3 px-8">
                    {currentAvatars.map((avatar) => {
                      const selected = avatar.id === avatarId;
                      return (
                        <button
                          key={avatar.id}
                          type="button"
                          onClick={() => setAvatarId(avatar.id)}
                          className={`group rounded-xl p-2 bg-white/5 border transition transform hover:-translate-y-1
                            ${selected
                              ? "border-indigo-400 ring-2 ring-indigo-500"
                              : "border-white/10 hover:border-white/40"
                            }`}
                        >
                          <div className="aspect-square rounded-lg overflow-hidden bg-gray-900 flex items-center justify-center">
                            <img
                              src={getAvatarUrl(avatar.seed)}
                              alt={avatar.label}
                              className="w-full h-full object-contain"
                            />
                          </div>
                          <p className="mt-1 text-[11px] text-gray-300 text-center leading-tight line-clamp-2">
                            {avatar.label}
                          </p>
                        </button>
                      );
                    })}
                  </div>

                  <p className="mt-3 text-[11px] text-gray-500 text-center">
                    Astuce : utilisez les flèches pour explorer les différents profils
                    (explorateurs, hackers, analystes…).
                  </p>
                  <p className="mt-2 text-[11px] text-gray-500 text-center">
                    Avatars générés avec{" "}
                    <a href="https://www.dicebear.com" className="underline">
                      DiceBear
                    </a>
                    , sous licence CC BY 4.0.
                  </p>
                </div>

                {/* Erreur globale création */}
                {error && (
                  <div className="rounded-lg border border-red-500/30 bg-red-500/10 text-red-300 text-sm px-3 py-2">
                    {error}
                  </div>
                )}

                {/* Boutons bas */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => navigate(`/play`)}
                    className="w-1/3 py-3 bg-gray-800 hover:bg-gray-700 rounded-lg font-semibold transition"
                  >
                    ← Retour
                  </button>
                  <button
                    type="submit"
                    disabled={!pseudo.trim() || !password.trim() || !avatarId}
                    className="w-2/3 py-3 bg-indigo-600 hover:bg-indigo-500 rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Rejoindre la session
                  </button>
                </div>

                <p className="text-[12px] text-gray-500 text-center mt-1">
                  Max 6 joueurs par session. Vous aurez besoin d&apos;un outil de
                  communication.
                </p>
              </form>
            </>
          )}

          {creationLocked && (
            <p className="text-sm text-amber-200 bg-amber-500/10 border border-amber-500/40 rounded-lg px-3 py-2">
              La partie est déjà en cours. Vous ne pouvez plus créer de nouveau joueur.
              Utilisez l&apos;une des cartes ci-dessus pour vous reconnecter à votre
              profil, ou contactez l&apos;organisateur si vous n&apos;avez jamais rejoint
              cette session.
            </p>
          )}
        </div>

        {/* Modale de reconnexion */}
        {selectedPlayer && (
          <PlayReconnectModal
            selectedPlayer={selectedPlayer}
            reconnectPassword={reconnectPassword}
            setReconnectPassword={setReconnectPassword}
            reconnectError={reconnectError}
            reconnectLoading={reconnectLoading}
            onReconnect={handleReconnect}
            onBypass={handleBypass}
            onClose={closeReconnect}
          />
        )}
      </div>
    </div>
  );
}

export default Play;
